import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/site/SiteLayout";
import Seo from "@/components/site/Seo";
import { CATEGORIES } from "@/data/categories";
import { RISKS } from "@/data/risks";
import { API } from "@/lib/api";

const SERVICES = [
  ["China Group Tours", "Canton Fair, Yiwu markets, factory belts, interpreters & networking"],
  ["China Sourcing", "Supplier hunting, factory visits, price negotiation & QC"],
  ["India · Vietnam · Thailand Sourcing", "Cluster mapping, supplier discovery & audits across the +1 countries"],
  ["Shipping Support", "Freight forwarding, customs, consolidation, door-to-door delivery"],
  ["Verification & Inspection", "License checks, factory-vs-trader, bank match, pre-shipment AQL"],
  ["Private Label Brand", "Branding, packaging, trademark guidance & product development"],
  ["China+1 Strategy", "Country benchmarking, duty & landed-cost comparison, dual sourcing"],
  ["Consultation", "One-on-one guidance for starting or scaling your import business"],
];

const GROUP_LABELS = { Category: "Categories", Guide: "Guides", Service: "Services", Risk: "Sourcing risks" };
const GROUP_ORDER = ["Category", "Guide", "Service", "Risk"];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = (params.get("q") || "").trim();
  const [term, setTerm] = useState(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("q") || "";
  });
  const [guides, setGuides] = useState([]);

  useEffect(() => { setTerm(q); }, [q]);
  useEffect(() => {
    fetch(`${API}/guides`).then((r) => r.json()).then((d) => setGuides(d.guides || [])).catch(() => {});
  }, []);

  const index = useMemo(() => {
    const items = [];
    CATEGORIES.forEach((c) => items.push({
      type: "Category", title: c.name, desc: c.metaDesc || c.positioning, to: `/categories/${c.slug}`,
      text: `${c.name} ${c.positioning || ""} ${c.keyword || ""} ${(c.range || []).join(" ")}`,
    }));
    SERVICES.forEach(([t, d]) => items.push({ type: "Service", title: t, desc: d, to: "/services", text: `${t} ${d}` }));
    RISKS.forEach(([, title, , conseq]) => items.push({ type: "Risk", title, desc: conseq, to: "/risks", text: `${title} ${conseq}` }));
    guides.forEach((g) => items.push({
      type: "Guide", title: g.title, desc: g.blurb, to: `/guides/${g.slug}`,
      text: `${g.title} ${g.blurb || ""} ${g.summary || ""} ${g.country || ""}`,
    }));
    return items;
  }, [guides]);

  const results = useMemo(() => {
    if (!q) return [];
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    return index.filter((it) => {
      const hay = `${it.title} ${it.text}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    });
  }, [q, index]);

  const submit = (e) => { e.preventDefault(); setParams(term.trim() ? { q: term.trim() } : {}); };

  return (
    <SiteLayout>
      <Seo
        title={q ? `Search: ${q} — The Sourcing Atlas` : "Search — The Sourcing Atlas"}
        description="Search categories, guides, services and sourcing risks across The Sourcing Atlas."
        path="/search"
        noindex
      />
      <div className="crumb"><Link to="/">Home</Link> › Search</div>
      <div className="pband">
        <div className="pband-in">
          <span className="tierpill tn">SEARCH</span>
          <h1>Search The Sourcing Atlas</h1>
          <form onSubmit={submit} data-testid="search-page-form" style={{ display: "flex", gap: 10, marginTop: 18, maxWidth: 560 }}>
            <input
              autoFocus
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Search products, countries, guides, services…"
              data-testid="search-page-input"
              style={{ minWidth: 0, flex: 1, border: "1.5px solid var(--line)", borderRadius: 999, padding: "12px 18px", fontSize: 15, fontFamily: "inherit", background: "#fff" }}
            />
            <button className="btn btn-red" type="submit" data-testid="search-page-submit">Search</button>
          </form>
        </div>
      </div>

      <section>
        <div className="wrap">
          {q && (
            <p style={{ color: "var(--grey)", fontSize: 14, marginBottom: 24 }} data-testid="search-count">
              {results.length} result{results.length === 1 ? "" : "s"} for “{q}”
            </p>
          )}
          {q && results.length === 0 && (
            <p data-testid="search-empty" style={{ color: "var(--grey)" }}>
              No matches. Try a product, country or service — for example “fasteners”, “Vietnam” or “Canton Fair”.
            </p>
          )}
          {!q && (
            <p style={{ color: "var(--grey)" }}>Type a product, country, service or guide name above to search the site.</p>
          )}

          {GROUP_ORDER.map((gr) => {
            const rows = results.filter((r) => r.type === gr);
            if (!rows.length) return null;
            return (
              <div key={gr} style={{ marginBottom: 30 }} data-testid={`search-group-${gr.toLowerCase()}`}>
                <div className="k2" style={{ textAlign: "left" }}>{GROUP_LABELS[gr]}</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 14, marginTop: 14 }}>
                  {rows.map((r, i) => (
                    <Link key={`${gr}-${i}`} to={r.to} data-testid={`search-result-${gr.toLowerCase()}-${i}`}
                      style={{ display: "block", background: "#fff", border: "1.5px solid var(--line)", borderRadius: 12, padding: 18, textDecoration: "none", boxShadow: "0 6px 18px rgba(29,60,115,.05)" }}>
                      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "var(--navy-lt, #2E63AE)", textTransform: "uppercase", letterSpacing: ".05em" }}>{r.type}</span>
                      <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 17, color: "var(--navy)", margin: "4px 0 6px" }}>{r.title}</h3>
                      <p style={{ fontSize: 13, color: "var(--grey)", margin: 0 }}>{(r.desc || "").slice(0, 140)}</p>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </SiteLayout>
  );
}
